import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Quiz.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

function clamp180(text) {
  if (text.length <= 180) return text;
  return text.slice(0, 180);
}

function wordCount(s) {
  return (s.trim().match(/\b[\w’'-]+\b/g) || []).length;
}

export default function Quiz() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [dir, setDir] = useState(0); // for slide animation
  const textareaRef = useRef(null);

  // Fetch questions
  useEffect(() => {
    let alive = true;
    async function run() {
      setLoading(true);
      setFetchError("");
      try {
        const res = await fetch(`${API_BASE_URL}/api/quiz_v2/questions`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        // data.questions may be an array of objects or a map; normalize to [{id, text}, ...]
        let normalized = [];
        if (Array.isArray(data.questions)) {
          normalized = data.questions.map((q, idx) => ({
            id: q.id ?? q.prompt_id ?? idx + 1,
            text: q.text ?? q.question ?? q.prompt ?? "",
          }));
        } else if (data.questions && typeof data.questions === "object") {
          normalized = Object.entries(data.questions).map(([k, v]) => ({
            id: Number.isNaN(Number(k)) ? k : Number(k),
            text: v,
          }));
        }

        // Filter out any empty
        normalized = normalized.filter(q => q.text && String(q.text).trim().length > 0);

        if (alive) {
          setQuestions(normalized);
          setLoading(false);
        }
      } catch (err) {
        if (alive) {
          setFetchError(`Could not load questions. ${err.message}`);
          setLoading(false);
        }
      }
    }
    run();
    return () => { alive = false; };
  }, []);

  const q = questions[current];
  const value = answers[q?.id] ?? "";
  const chars = value.length;
  const words = wordCount(value);

  const canProceed = useMemo(() => {
    // Hard stop at >= 5 words & 1–180 chars (non-empty)
    return words >= 5 && chars > 0 && chars <= 180;
  }, [words, chars]);

  useEffect(() => {
    // focus textarea on slide in
    if (textareaRef.current) textareaRef.current.focus();
  }, [current]);

  function onInput(e) {
    const next = clamp180(e.target.value);
    setAnswers(prev => ({ ...prev, [q.id]: next }));
  }

  async function onNext() {
    if (!canProceed) return;
    if (current < questions.length - 1) {
      setDir(1);
      setCurrent(c => c + 1);
      return;
    }
    // Submit
    setSubmitting(true);
    setSubmitError("");
    try {
      // Shape: { answers: { [id]: "text" }, meta: {...} }
      const payload = {
        answers: Object.fromEntries(
          Object.entries(answers).map(([k, v]) => [Number.isNaN(Number(k)) ? k : Number(k), v])
        ),
        meta: { total_questions: questions.length }
      };

      const res = await fetch(`${API_BASE_URL}/api/quiz_v2/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status} – ${txt.slice(0, 200)}`);
      }
      const data = await res.json();

      // Persist result for SignupForm to render
      try {
        localStorage.setItem("quizAnswers", JSON.stringify(payload.answers || {}));
        localStorage.setItem("quizScores", JSON.stringify((data && (data.axes_percent ?? data.axes)) || {}));
        localStorage.setItem("vybly_quiz_results", JSON.stringify({
          scored_at: new Date().toISOString(),
          result: data
        }));
      } catch (e) {
        console.warn("Failed to persist quiz data", e);
      }

      // Navigate to signup to show results
      navigate("/results", { replace: true });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  function onBack() {
    if (current === 0) return;
    setDir(-1);
    setCurrent(c => c - 1);
  }

  if (loading) {
    return (
      <div className="quiz__wrap">
        <div className="quiz__container">
          <div className="quiz__skeleton">
            <div className="quiz__skeleton-line w60" />
            <div className="quiz__skeleton-line w90" />
            <div className="quiz__skeleton-line w75" />
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="quiz__wrap">
        <div className="quiz__container">
          <div className="quiz__error">
            <h2>Couldn’t load the quiz</h2>
            <p>{fetchError}</p>
            <button className="btn btn--primary" onClick={() => window.location.reload()}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="quiz__wrap">
        <div className="quiz__container">
          <div className="quiz__error">
            <h2>No questions</h2>
            <p>Questions were not provided by the server.</p>
          </div>
        </div>
      </div>
    );
  }

  const pct = Math.round(((current + 1) / questions.length) * 100);

  return (
    <div className="quiz__wrap">
      <div className="quiz__container">
        <header className="quiz__header">
          <div className="quiz__brand">
            <span className="quiz__logo-dot" />
            <span className="quiz__brand-text">Vybly</span>
          </div>
          <div className="quiz__progress">
            <div className="quiz__progress-bar" style={{ width: `${pct}%` }} />
            <div className="quiz__progress-meta">
              <span>Question {current + 1} / {questions.length}</span>
              <span>{pct}%</span>
            </div>
          </div>
        </header>

        <main className={`quiz__card ${dir === 1 ? "slide-in-right" : dir === -1 ? "slide-in-left" : ""}`}>
          <h1 className="quiz__title">Quick DeepDive</h1>
          <p className="quiz__subtitle">We start with a quick, research-backed DeepDive to tailor your experience. No credit card required.</p>
          <div className="quiz__prompt">
            {q.text}
          </div>

          <label htmlFor="answer" className="quiz__label">Your answer</label>
          <textarea
            ref={textareaRef}
            id="answer"
            className="quiz__textarea"
            maxLength={180}
            placeholder="Write one sentence (≤180 characters). Keep it simple, human, and honest..."
            value={value}
            onChange={onInput}
          />
          <div className="quiz__meta">
            <span>{chars}/180</span>
            <span>{words} {words === 1 ? "word" : "words"} (min 5)</span>
          </div>

          {submitError ? <div className="quiz__submit-error">{submitError}</div> : null}

          <div className="quiz__actions">
            <button className="btn btn--ghost" onClick={onBack} disabled={current === 0 || submitting}>
              Back
            </button>
            <button
              className="btn btn--primary"
              onClick={onNext}
              disabled={!canProceed || submitting}
            >
              {current === questions.length - 1 ? (submitting ? "Submitting…" : "See my results") : "Next"}
            </button>
          </div>

          <footer className="quiz__footer">
            <span className="quiz__privacy">Private by default • No selling data • Optional anonymity</span>
          </footer>
        </main>
      </div>
    </div>
  );
}
